import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { documentApi, profileApi, resultsApi, schoolApi } from '../../api/endpoints.js';
import { useAuth } from '../../state/AuthContext.jsx';
import { useNotice } from '../../state/NoticeContext.jsx';
import { saveSlipDraft } from '../../utils/slipDraft.js';
import {
  Button,
  Card,
  Chips,
  Grid2,
  Muted,
  Select,
  Spinner,
  Text,
  fmtDate,
  useAsync
} from '../../ui.jsx';

// The backend only accepts these four values, so this must stay a fixed choice
// rather than free text, or saving the profile fails validation.
const EXAMS = ['WASSCE', 'SSSCE', 'IB', 'A-Level'];

const DOCUMENTS = [
  ['photo', 'Passport photograph'],
  ['ghanaCard', 'Ghana Card'],
  ['resultSlip', 'WASSCE result slip'],
  ['referenceLetter', 'Reference letter']
];

const REGIONS = [
  'Ahafo', 'Ashanti', 'Bono', 'Bono East', 'Central', 'Eastern', 'Greater Accra', 'North East',
  'Northern', 'Oti', 'Savannah', 'Upper East', 'Upper West', 'Volta', 'Western', 'Western North'
];

export default function Profile() {
  const notice = useNotice();
  const navigate = useNavigate();
  const { user } = useAuth();
  const reference = useAsync(() => schoolApi.reference(), []);
  const current = useAsync(() => profileApi.get(), []);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState('');
  const [readingSlip, setReadingSlip] = useState(false);
  const [slipRead, setSlipRead] = useState(null);
  const [oversize, setOversize] = useState(null);
  const limitMb = reference.data?.uploadLimitMb || 5;
  const compressUrl = reference.data?.compressUrl || 'https://www.slim-file.com/compress';

  const profile = current.data?.profile;
  const value = form || {
    fullName: profile?.fullName || user?.name || '',
    dateOfBirth: profile?.dateOfBirth || '',
    gender: profile?.gender || '',
    nationality: profile?.nationality || 'Ghanaian',
    ghanaCardNumber: profile?.ghanaCardNumber || '',
    specialNeeds: profile?.specialNeeds || '',
    region: profile?.region || '',
    homeTown: profile?.homeTown || '',
    phone: profile?.phone || user?.phone || '',
    email: profile?.email || user?.email || '',
    guardianName: profile?.guardianName || '',
    guardianPhone: profile?.guardianPhone || '',
    exam: profile?.exam || 'WASSCE',
    indexNumber: profile?.indexNumber || '',
    previousSchool: profile?.previousSchool || '',
    examYear: profile?.examYear || '',
    interests: profile?.interests || [],
    careerGoal: profile?.careerGoal || '',
    preferredRegion: profile?.preferredRegion || '',
    // The API stores 'Yes'/'No', not a boolean. A boolean here was rejected by the
    // enum, so the whole profile save failed.
    openToFeePaying: profile?.openToFeePaying || 'Yes'
  };

  const set = (patch) => setForm({ ...value, ...patch });
  const setField = (key) => (event) => set({ [key]: event.target.value });

  if (current.loading || reference.loading) return <Spinner label="Loading your profile" />;

  const interests = reference.data?.interests || {};
  const common = profile?.commonDocuments || {};
  const slipBusy = readingSlip || uploading === 'resultSlip';
  const savedSlip = common.resultSlip;
  const savedSlipCanBeRead = ['image/', 'application/pdf'].some((prefix) =>
    String(savedSlip?.mimeType || '').startsWith(prefix)
  );

  const save = async (event) => {
    event.preventDefault();
    const missingCompulsory = [
      ['Gender', value.gender],
      ['Nationality', value.nationality],
      ['Ghana Card number', value.ghanaCardNumber],
      ['Region', value.region],
      ['Phone', value.phone]
    ].filter(([, v]) => !String(v ?? '').trim());
    if (missingCompulsory.length) {
      notice.error(`Please fill in: ${missingCompulsory.map(([label]) => label).join(', ')}.`);
      return;
    }
    setBusy(true);
    try {
      const data = await profileApi.save({
        ...value,
        examYear: value.examYear || null,
        dateOfBirth: value.dateOfBirth || null
      });
      current.setData({ profile: data.profile });
      setForm(null);
      notice.success(
        data.missingDocuments?.length
          ? `Profile saved. Still needed: ${data.missingDocuments.length} document(s).`
          : 'Profile saved. It is complete.'
      );
    } catch (error) {
      notice.fromError(error);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Read a result slip the student has uploaded. The extraction is never saved
   * here: it is handed to Match as a draft so the student can check every grade
   * before it counts. A failed read still leaves the document on the profile.
   */
  const readSlip = async (documentId) => {
    setReadingSlip(true);
    try {
      const read = await resultsApi.readSlip(documentId);
      if (!read.read) {
        setSlipRead(null);
        notice.error('Slip saved, but Entrance could not read it. Enter your grades on Match.');
        return false;
      }

      saveSlipDraft({
        result: read.result,
        message: read.message,
        name: read.name,
        indexNumber: read.index,
        year: read.year
      });
      setSlipRead({
        message: read.message,
        read: read.result
      });
      notice.success(read.message || 'Slip read. Check the grades on Match before saving.');
      return true;
    } catch (error) {
      setSlipRead(null);
      notice.fromError(error);
      return false;
    } finally {
      setReadingSlip(false);
    }
  };

  const upload = async (event, kind) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    // Check before uploading. Without this the whole file is sent, buffered in
    // memory by multer and only then rejected, so the user waits out a transfer
    // that was always going to fail. The warning stays on screen because it
    // holds a link they still have to click.
    if (file.size > limitMb * 1024 * 1024) {
      setOversize({ name: file.name, mb: (file.size / 1024 / 1024).toFixed(1) });
      return;
    }
    setOversize(null);

    setUploading(kind);
    try {
      const uploaded = await documentApi.upload(file, kind);
      await current.reload();

      if (kind === 'resultSlip') {
        const id = uploaded.document?.id || uploaded.document?._id;
        if (id) {
          await readSlip(id);
          return;
        }
        notice.success('Result slip uploaded. Enter your grades on Match.');
        return;
      }

      notice.success('Document uploaded.');
    } catch (error) {
      notice.fromError(error);
    } finally {
      setUploading('');
    }
  };

  const remove = async (id) => {
    try {
      await documentApi.remove(id);
      await current.reload();
      notice.success('Document removed.');
    } catch (error) {
      notice.fromError(error);
    }
  };

  const toggleInterest = (code) => {
    const list = value.interests.includes(code)
      ? value.interests.filter((c) => c !== code)
      : [...value.interests, code];
    set({ interests: list });
  };

  return (
    <>
      <h2>Your master profile</h2>
      <Muted>
        Saved once and shared only with the schools you apply to. Each school still gets its own
        application, questions and payment.
      </Muted>

      <form onSubmit={save}>
        <Card>
          <h3 style={{ fontSize: 18, margin: '0 0 12px' }}>About you</h3>
          <Grid2>
            <Text label="Full name" required value={value.fullName} onChange={setField('fullName')} />
            <Text
              label="Date of birth"
              type="date"
              value={value.dateOfBirth || ''}
              onChange={setField('dateOfBirth')}
            />
            <Select label="Gender" required value={value.gender} onChange={setField('gender')}>
              <option value="">Not set</option>
              <option value="Female">Female</option>
              <option value="Male">Male</option>
            </Select>
            <Text label="Nationality" required value={value.nationality} onChange={setField('nationality')} />
            <Text
              label="Ghana Card number"
              required
              value={value.ghanaCardNumber}
              onChange={setField('ghanaCardNumber')}
            />
            <Select label="Region" required value={value.region} onChange={setField('region')}>
              <option value="">Not set</option>
              {REGIONS.map((region) => (
                <option key={region} value={region}>{region}</option>
              ))}
            </Select>
            <Text label="Home town" value={value.homeTown} onChange={setField('homeTown')} />
            <Text
              label="Phone"
              type="tel"
              required
              hint="A Ghana number, for example 024 123 4567."
              value={value.phone}
              onChange={setField('phone')}
            />
            <Text label="Email" type="email" value={value.email} onChange={setField('email')} />
            <Text label="Guardian name" value={value.guardianName} onChange={setField('guardianName')} />
            <Text
              label="Guardian phone"
              type="tel"
              value={value.guardianPhone}
              onChange={setField('guardianPhone')}
            />
          </Grid2>
        </Card>

        <Card>
          <h3 style={{ fontSize: 18, margin: '0 0 12px' }}>Your education</h3>
          <Grid2>
            <Select label="Examination" value={value.exam} onChange={setField('exam')}>
              <option value="">Not set</option>
              {EXAMS.map((exam) => <option key={exam} value={exam}>{exam}</option>)}
            </Select>
            <Text
              label="Examination year"
              type="number"
              min="2000"
              max="2100"
              value={value.examYear}
              onChange={setField('examYear')}
            />
            <Text label="Index number" value={value.indexNumber} onChange={setField('indexNumber')} />
            <Text label="Previous school" value={value.previousSchool} onChange={setField('previousSchool')} />
          </Grid2>

          <div className="note" style={{ marginTop: 12 }}>
            <b>Have your result slip?</b>
            <div className="mu" style={{ margin: '4px 0 10px' }}>
              Upload a clear photo, screenshot or PDF. Entrance reads it and fills your grades on the
              Match page so you only need to check them.
            </div>
            <div className="row">
              <label
                className="btn sm"
                style={{ margin: 0, cursor: slipBusy ? 'wait' : 'pointer', display: 'inline-block' }}
              >
                {slipBusy ? 'Reading slip...' : savedSlip ? 'Upload a different slip' : 'Upload result slip'}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => upload(e, 'resultSlip')}
                  disabled={slipBusy}
                />
              </label>
              {savedSlipCanBeRead && (savedSlip?._id || savedSlip?.id) ? (
                <Button
                  size="sm"
                  variant="s"
                  busy={readingSlip}
                  onClick={() => readSlip(savedSlip._id || savedSlip.id)}
                >
                  Read uploaded slip
                </Button>
              ) : null}
            </div>
            {slipRead ? (
              <div className="note" style={{ marginTop: 10 }}>
                <b>Grades ready</b>
                <div className="mu" style={{ marginTop: 4 }}>
                  {slipRead.message || 'Entrance read your slip.'} Check and save them on the Match page.
                </div>
                <div style={{ marginTop: 8 }}>
                  <Button size="sm" onClick={() => navigate('/match')}>
                    Review and save results
                  </Button>
                </div>
              </div>
            ) : null}
          </div>

          <Muted>
            Your grades are on the Match page, where Entrance can check them against each programme.
          </Muted>
        </Card>

        <Card>
          <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>What you want to study</h3>
          <Muted>Entrance uses this to suggest programmes that fit your grades and interests.</Muted>
          <div className="row">
            {Object.entries(interests).map(([code, label]) => (
              <button
                key={code}
                type="button"
                className={`chip ${value.interests.includes(code) ? 'on' : ''}`}
                aria-pressed={value.interests.includes(code)}
                onClick={() => toggleInterest(code)}
              >
                {label}
              </button>
            ))}
          </div>
          <Grid2>
            <Text
              label="Career goal"
              hint="Optional."
              value={value.careerGoal}
              onChange={setField('careerGoal')}
            />
            <Select
              label="Preferred region to study"
              value={value.preferredRegion}
              onChange={setField('preferredRegion')}
            >
              <option value="">No preference</option>
              {[...REGIONS, 'Any region'].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </Select>
            <Select
              label="Open to fee-paying places?"
              value={value.openToFeePaying === 'No' ? 'No' : 'Yes'}
              onChange={(e) => set({ openToFeePaying: e.target.value })}
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </Select>
          </Grid2>
        </Card>

        <Button type="submit" busy={busy}>Save profile</Button>
      </form>

      <Card style={{ marginTop: 14 }}>
        <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Your documents</h3>
        <Muted>
          These four are reused for every school. Each application can add documents the school asks
          for separately.
        </Muted>
        {oversize ? (
          <div className="banner warn" style={{ margin: '10px 0' }}>
            <b>{oversize.name}</b> is {oversize.mb} MB, over the {limitMb} MB limit, so it was not
            uploaded. Compress it first, then choose the smaller file here.
            <div style={{ marginTop: 8 }}>
              <a className="btn sm" href={compressUrl} target="_blank" rel="noreferrer">
                Compress with SlimFile
              </a>{' '}
              <button type="button" className="lk" onClick={() => setOversize(null)}>
                Dismiss
              </button>
            </div>
          </div>
        ) : null}
        {DOCUMENTS.map(([kind, label]) => {
          // GET /api/profile populates commonDocuments, so each entry is the
          // document itself rather than a bare id.
          const doc = common[kind];
          return (
            <div className="prog" key={kind}>
              <div style={{ flex: 1 }}>
                <b>{label}</b>
                <div className="mu">
                  {doc ? `Uploaded ${fmtDate(doc.createdAt || doc.uploadedAt)}` : 'Not uploaded'}
                </div>
              </div>
              <div className="row" style={{ flexWrap: 'nowrap' }}>
                {doc?._id || doc?.id ? (
                  <Button size="sm" variant="s" onClick={() => remove(doc._id || doc.id)}>
                    Remove
                  </Button>
                ) : null}
                <label className="btn sm" style={{ margin: 0, cursor: 'pointer', display: 'inline-block' }}>
                  {uploading === kind ? 'Uploading...' : doc ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    style={{ display: 'none' }}
                    onChange={(e) => upload(e, kind)}
                    disabled={uploading === kind}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </Card>
    </>
  );
}
